import { $, $$ } from './dom.js';
import { ct } from './i18n.js';
import { api } from './api.js';
import { revealSection } from './section-nav.js';

/**
 * The guided tour of a module page.
 *
 * Thirty-seven modules, each opening on a settings page nobody has introduced.
 * The explanation existed — four steps per module, written and translated into
 * sixteen languages — but it lived on the public showcase page: read before
 * installing, never while configuring.
 *
 * So the tour shows those very steps, here, pointing at the thing each one
 * talks about. It runs once per module per account, and the header keeps a
 * button to replay it for ever after.
 */

interface TourStep {
  title: string;
  text: string;
}

export interface TourData {
  module: string | null;
  steps: TourStep[];
  seen: boolean;
}

/** Room left around the highlighted element, in pixels. */
const PADDING = 6;

let active: (() => void) | null = null;

export function initTour(): void {
  const data = readTourData();
  if (!data) return;

  /*
   * A page with panels builds its own steps, so the guide's are only needed
   * where there are none. Refusing on an empty payload would have silenced the
   * tour on every page that explains itself best.
   */
  if (data.steps.length === 0 && $$('main section.panel').length === 0) return;

  const replay = $<HTMLButtonElement>('[data-tour-replay]');
  replay?.addEventListener('click', () => {
    start(data);
  });

  // First visit only. A tour that opens every time is a tour people learn to
  // close without reading, which is worse than no tour at all.
  if (!data.seen) start(data);
}

function readTourData(): TourData | null {
  const node = document.getElementById('tour-data');
  if (!node?.textContent) return null;

  try {
    return JSON.parse(node.textContent) as TourData;
  } catch {
    // A malformed block must cost nothing: the page is a settings page first.
    return null;
  }
}

/** One field of a compartment, as the card explains it. */
export interface StepField {
  name: string;
  hint: string;
  /** A placeholder, when the page offers one: `1d`, `https://…`, `{user}`. */
  example: string;
}

/** One card of the tour: what it says, what it details, and what it points at. */
export interface Step {
  title: string;
  text: string;
  fields: StepField[];
  /** The label of the button that creates a row here, when there is one. */
  action: string;
  anchor: HTMLElement | null;
}

/**
 * The steps, read from the page itself.
 *
 * The first version paired the module guide's steps with the page's panels by
 * index, and that was wrong in both directions at once. The Roles page carries
 * **nine** compartments and its guide has three steps: six compartments were
 * never explained, and the third card — about persistent roles — was drawn over
 * the temporary ones. A tour that describes the wrong panel is worse than none,
 * because it is believed.
 *
 * So the text and the highlight now come from the **same element**. Every panel
 * already carries a title and, almost always, a one-line subtitle written for
 * that panel and translated into sixteen languages: that is the explanation,
 * standing right where it applies. A mismatch has become impossible to write.
 */
export function buildSteps(data: TourData): Step[] {
  const panels = $$<HTMLElement>('main section.panel').filter((panel) => titleOf(panel) !== '');

  /*
   * Pages with no panel — a list of tickets, a gallery — keep the guide's
   * steps, centred. They have nothing to point at, and a tour that says
   * something general beats a page that says nothing at all.
   */
  if (panels.length === 0) {
    return data.steps.map((step) => ({ ...step, fields: [], action: '', anchor: null }));
  }

  const heading = $<HTMLElement>('main h1');
  const subheading = heading?.parentElement?.querySelector('p');

  const intro: Step[] = heading
    ? [
        {
          title: heading.textContent ?? '',
          text: subheading?.textContent ?? '',
          fields: [],
          action: '',
          anchor: null,
        },
      ]
    : [];

  return [
    ...intro,
    ...panels.map((panel) => ({
      title: titleOf(panel),
      text: $('.panel-subtitle', panel)?.textContent?.trim() ?? '',
      fields: fieldsOf(panel),
      action:
        $<HTMLElement>(
          '[data-add], [data-add-question], [data-add-reaction]',
          panel,
        )?.textContent?.trim() ?? '',
      anchor: panel,
    })),
  ];
}

/** How many fields a card lists before it starts hiding them. */
const MAX_FIELDS = 6;

/**
 * What each entry of a compartment expects.
 *
 * A card that repeats the panel's one-line subtitle is a table of contents:
 * it names the compartment and leaves the person facing six empty inputs. The
 * answer was already on the page — every field carries a label, most carry a
 * hint under them, and several carry a placeholder that is a working example.
 * The card gathers those, so the explanation stands beside the thing it
 * explains instead of somewhere else.
 *
 * Capped at six: past that it stops being an explanation and becomes the page
 * printed twice.
 */
function fieldsOf(panel: HTMLElement): StepField[] {
  const labels = [
    ...$$<HTMLElement>('.field-label, .option-card-title', panel),
    ...blankRow(panel),
  ];

  /*
   * One line per name.
   *
   * A compartment holding one saved rule *and* the template of a new one lists
   * the same labels twice — the reader sees "Role, Role, Duration, Duration"
   * and learns nothing from the repetition.
   */
  const seen = new Set<string>();
  const unique = labels.filter((label) => {
    const name = label.textContent?.trim() ?? '';
    if (!name || seen.has(name)) return false;

    seen.add(name);
    return true;
  });

  return unique.slice(0, MAX_FIELDS).map((label) => {
    /*
     * The hint is looked up in the field's own wrapper, never further.
     * A page is a grid of two-column cells; searching wider would pair a label
     * with the hint of the field beside it, which is worse than no hint at all.
     */
    /*
     * Climbed from the **parent**, not from the label.
     *
     * A `field-label` is itself a `<label>`, so `closest` answered with the
     * label — and every hint search then ran inside an element that holds only
     * the field's name. Every card came out as a bare list of labels, which is
     * the table of contents this change exists to end.
     */
    const wrapper = label.parentElement?.closest<HTMLElement>('div, label') ?? panel;

    return {
      name: label.textContent?.trim() ?? '',
      /*
       * A label takes its own kind of hint, never the other's.
       *
       * A `field-label` sitting above a row of option cards would otherwise
       * borrow the first card's hint: "Mode — granted now, removed at the
       * deadline" reads perfectly and describes one option out of two. The
       * cards are listed on their own lines anyway, each with its sentence.
       */
      hint:
        $(
          label.classList.contains('option-card-title') ? '.option-card-hint' : '.field-hint',
          wrapper,
        )?.textContent?.trim() ?? genericHint(wrapper),
      example: $<HTMLInputElement>('[placeholder]', wrapper)?.placeholder.trim() ?? '',
    };
  });
}

/**
 * The fields of a rule nobody has created yet.
 *
 * Half the modules show nothing until something exists: a role rule, an
 * automod rule, a shop item, a menu entry. Their compartments were therefore
 * explained by a title and an empty body — the tour was thinnest exactly where
 * the module is hardest, which is what the complaint said.
 *
 * Those pages already ship the answer: the row is cloned from a `<template>`
 * rendered by the server, so the labels and the hints of a rule exist in the
 * page before the first rule does. The panel names its template through the
 * button that adds one — `data-add="temporary"` finds
 * `template[data-template="temporary"]` — which is a link the page already
 * maintains rather than a guess made here.
 */
function blankRow(panel: HTMLElement): HTMLElement[] {
  const kinds = $$<HTMLElement>('[data-add]', panel)
    .map((button) => button.dataset.add ?? '')
    .filter(Boolean);

  return kinds.flatMap((kind) => {
    const template = $<HTMLTemplateElement>(`template[data-template="${kind}"]`);
    if (!template) return [];

    return [...template.content.querySelectorAll<HTMLElement>('.field-label, .option-card-title')];
  });
}

/**
 * What a field wants when the page does not say.
 *
 * Half the labelled fields carry no hint, and most of those are the same three
 * kinds repeated: a channel, a role, a colour. Writing a sentence under each on
 * the page would be the clutter this feature was asked to avoid — four "the
 * channel where the bot will write" stacked on the Welcome page, in sixteen
 * languages.
 *
 * So the sentence lives in the tour only, chosen by the **kind of control**
 * rather than by the label: kinds are unambiguous, and a label is translated.
 */
function genericHint(wrapper: HTMLElement): string {
  if ($('input[type="color"]', wrapper)) return ct('tourFieldColour', 'The embed’s colour bar.');

  /*
   * Matched on the field's `name` as well as on its widget.
   *
   * Half the channel pickers are plain `<select>` elements filled by the
   * server, with nothing to tell them apart but the field they save into —
   * `welcome.channelId`, `dm.channelId`. Looking only for the enhanced
   * component left the four blocks of the Welcome page unexplained, which is
   * the page somebody configures first.
   */
  const field = $<HTMLInputElement | HTMLSelectElement>('[name], [data-field]', wrapper);
  const name = field?.getAttribute('name') ?? field?.getAttribute('data-field') ?? '';

  if (
    $('[data-select-source="channels"], [data-channel-picker]', wrapper) ||
    /channelid$/i.test(name)
  ) {
    return ct('tourFieldChannel', 'The channel the bot will write in.');
  }

  if ($('[data-select-source="roles"], [data-role-picker]', wrapper) || /roleid[s]?$/i.test(name)) {
    return ct('tourFieldRole', 'The role this setting acts on.');
  }

  if (/\.title$/i.test(name)) return ct('tourFieldTitle', 'The bold line at the top of the embed.');
  if (/\.description$/i.test(name)) {
    return ct(
      'tourFieldDescription',
      'The body of the embed. Placeholders such as {user} are replaced.',
    );
  }

  return '';
}

/**
 * What to call a compartment.
 *
 * Three shapes exist, and only the first was read at first: on a page split
 * into tabs most panels carry no `panel-title` at all — their name is the tab
 * label, and the heading inside is a `field-group-title`. Reading only the
 * first shape gave the Reports page a one-step tour of a four-compartment page,
 * which is the "you do not do everything" half of the complaint.
 *
 * The group heading comes before the tab label because it is the more precise
 * of the two: a compartment holding three panels would otherwise repeat its own
 * name three times.
 */
function titleOf(panel: HTMLElement): string {
  const own = $('.panel-title', panel)?.textContent?.trim();
  if (own) return own;

  const group = $('.field-group-title', panel)?.textContent?.trim();
  if (group) return group;

  /*
   * A heading in the panel's header, whatever class it wears.
   *
   * The Logging page titled its two compartments with a bare `font-semibold`
   * rather than `panel-title` — a styling slip, invisible everywhere else, that
   * cost the page its whole tour. The page is fixed; this fallback means the
   * next slip costs nothing.
   */
  const heading = $('.panel-header h2', panel)?.textContent?.trim();
  if (heading) return heading;

  return panel.closest<HTMLElement>('[data-section]')?.dataset.sectionLabel?.trim() ?? '';
}

/**
 * Opens whatever is holding the anchor closed.
 *
 * A tabbed compartment, or a `<details>` folded shut. Pointing at an element
 * nobody can see is the failure this whole feature exists to avoid, and it is
 * silent: the spotlight lands on an empty rectangle, or on the panel that took
 * its place.
 */
function reveal(anchor: HTMLElement): void {
  revealSection(anchor);

  for (const details of ancestors(anchor)) details.open = true;
}

/** The folded `<details>` an element sits inside, outermost first. */
function ancestors(anchor: HTMLElement): HTMLDetailsElement[] {
  const out: HTMLDetailsElement[] = [];

  let current: HTMLElement | null = anchor;
  while (current) {
    const details: HTMLDetailsElement | null = current.closest('details');
    if (!details) break;

    if (!details.open) out.unshift(details);
    current = details.parentElement;
  }

  return out;
}

function start(data: TourData): void {
  // Replaying while a tour is open would stack two overlays and two key
  // listeners, and the second Escape would close only one of them.
  active?.();

  const steps = buildSteps(data);
  if (steps.length === 0) return;

  let index = 0;

  const overlay = document.createElement('div');
  overlay.className = 'tour-overlay';

  const spotlight = document.createElement('div');
  spotlight.className = 'tour-spotlight';

  const card = document.createElement('div');
  card.className = 'tour-card';
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'false');
  card.setAttribute('aria-live', 'polite');

  const title = document.createElement('p');
  title.className = 'tour-title';

  const text = document.createElement('p');
  text.className = 'tour-text';

  /*
   * The detail of the compartment: one line per entry, "name — what it wants",
   * with the page's own placeholder as an example when it offers one. This is
   * the difference between a tour and a table of contents.
   */
  const fields = document.createElement('ul');
  fields.className = 'tour-fields';

  const counter = document.createElement('span');
  counter.className = 'tour-counter';

  const skip = button('btn-ghost', ct('tourSkip', 'Skip'));
  const back = button('btn-ghost', ct('tourBack', 'Back'));
  const next = button('btn-primary', ct('tourNext', 'Next'));

  const actions = document.createElement('div');
  actions.className = 'tour-actions';
  actions.append(counter, skip, back, next);

  card.append(title, text, fields, actions);
  overlay.append(spotlight, card);
  document.body.append(overlay);

  const close = () => {
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('resize', place);
    window.removeEventListener('scroll', place, true);
    overlay.remove();
    active = null;

    void markSeen(data.module);
  };

  active = close;

  function onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') close();
    if (event.key === 'ArrowRight') move(1);
    if (event.key === 'ArrowLeft') move(-1);
  }

  function move(delta: number): void {
    const target = index + delta;
    if (target < 0) return;
    if (target >= steps.length) return close();

    index = target;
    render();
  }

  function render(): void {
    const step = steps[index];
    if (!step) return close();

    title.textContent = step.title;
    text.textContent = step.text;

    fields.replaceChildren();
    for (const field of step.fields) {
      const item = document.createElement('li');

      const name = document.createElement('b');
      name.textContent = field.name;
      item.append(name);

      if (field.hint) item.append(`: ${field.hint}`);
      if (field.example) {
        const example = document.createElement('code');
        example.textContent = field.example;
        item.append(' ', ct('tourExample', 'e.g.'), ' ', example);
      }

      fields.append(item);
    }

    /*
     * The button that brings the fields into being.
     *
     * On a page where nothing exists yet, the card has just described a rule
     * the reader cannot see. Naming the button is what turns that description
     * into something they can act on.
     */
    if (step.action) {
      const action = document.createElement('li');
      action.className = 'tour-action';
      action.textContent = ct('tourAddRow', 'Add one with “{button}”.', { button: step.action });
      fields.append(action);
    }

    const shown = $$<HTMLElement>('.field-label, .option-card-title', step.anchor ?? document.body);
    if (shown.length > step.fields.length && step.fields.length > 0) {
      const more = document.createElement('li');
      more.className = 'tour-more';
      more.textContent = ct('tourMoreFields', '…and {count} more on the page.', {
        count: String(shown.length - step.fields.length),
      });
      fields.append(more);
    }
    counter.textContent = `${String(index + 1)} / ${String(steps.length)}`;

    back.disabled = index === 0;
    next.textContent =
      index === steps.length - 1 ? ct('tourDone', 'Got it') : ct('tourNext', 'Next');

    const anchor = step.anchor;

    if (anchor) {
      // Open first, measure second: a hidden element has no useful rectangle,
      // and the spotlight would be drawn around a zero-sized box.
      reveal(anchor);
      anchor.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    place();
  }

  /** Puts the spotlight over the current anchor, and the card beside it. */
  function place(): void {
    const anchor = steps[index]?.anchor ?? null;

    if (!anchor) {
      spotlight.classList.add('hidden');
      card.classList.add('tour-card-centred');
      card.style.removeProperty('top');
      card.style.removeProperty('left');
      return;
    }

    const box = anchor.getBoundingClientRect();

    spotlight.classList.remove('hidden');
    spotlight.style.top = `${String(box.top - PADDING)}px`;
    spotlight.style.left = `${String(box.left - PADDING)}px`;
    spotlight.style.width = `${String(box.width + PADDING * 2)}px`;
    spotlight.style.height = `${String(box.height + PADDING * 2)}px`;

    card.classList.remove('tour-card-centred');

    /*
     * Below the anchor, unless there is no room left underneath — in which case
     * above it. Measured against the viewport rather than the document: the
     * card is fixed, and a page scrolled halfway would otherwise place it off
     * screen.
     */
    const below = box.bottom + 12;
    const fits = below + card.offsetHeight < window.innerHeight - 12;

    card.style.top = `${String(fits ? below : Math.max(12, box.top - card.offsetHeight - 12))}px`;
    card.style.left = `${String(Math.max(12, Math.min(box.left, window.innerWidth - card.offsetWidth - 12)))}px`;
  }

  skip.addEventListener('click', close);
  back.addEventListener('click', () => {
    move(-1);
  });
  next.addEventListener('click', () => {
    move(1);
  });

  // A click on the backdrop, but never on the card itself.
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close();
  });

  window.addEventListener('keydown', onKey);
  window.addEventListener('resize', place);
  window.addEventListener('scroll', place, true);

  render();
  next.focus();
}

function button(variant: string, label: string): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = `btn btn-sm ${variant}`;
  element.textContent = label;
  return element;
}

/**
 * Remembers that this account has been through the tour.
 *
 * Failures are swallowed: the worst outcome is seeing the tour once more, and
 * an error toast about a help feature would be noise on a page somebody came to
 * configure.
 */
async function markSeen(module: string | null): Promise<void> {
  if (!module) return;

  const guildId = new URLSearchParams(window.location.search).get('guildId') ?? '';
  if (!guildId) return;

  try {
    await api.post(`/api/tours/seen?guildId=${guildId}`, { guildId, module });
  } catch {
    // Deliberately silent, see above.
  }
}
